import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { 
  Target, BookOpen, FileText, Gamepad2, 
  Loader2, Lock, Play, Brain, Heart, Activity,
  Droplet, Zap, ArrowLeft, ChevronLeft, ChevronRight
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { toast } from 'sonner';

const API = process.env.REACT_APP_BACKEND_URL;

// Helper to get full URL for uploaded assets
const getFullUrl = (url) => {
  if (!url) return null;
  if (url.startsWith('http')) return url;
  if (url.startsWith('/')) return `${API}${url.startsWith('/api') ? '' : '/api'}${url}`;
  return `${API}/api/${url}`;
};

// Brand Logo component that uses uploaded logo
const BrandLogo = ({ logoUrl, dark = true }) => (
  <div className="flex items-center gap-2">
    <img src={logoUrl} alt="MedStudyPro" className="h-8 object-contain" />
    <span className={`text-xl font-bold ${dark ? 'text-white' : 'text-[#1E3A5C]'}`}>MedStudyPro</span>
  </div>
);

// Default MedStudyPro Logo Component - Updated geometric M design
const DefaultLogo = ({ dark = true }) => (
  <div className="flex items-center gap-2">
    <svg viewBox="0 0 40 40" className="h-8 w-8" fill="none" xmlns="http://www.w3.org/2000/svg">
      {/* Left M leg */}
      <path d="M4 32V10L12 18L20 10" stroke="#7DD3FC" strokeWidth="4" strokeLinecap="round" strokeLinejoin="round"/>
      {/* Right M leg */}
      <path d="M20 10L28 18L36 10V32" stroke="#7DD3FC" strokeWidth="4" strokeLinecap="round" strokeLinejoin="round"/>
      {/* Center cube - top face */}
      <path d="M20 16L24 18V22L20 24L16 22V18L20 16Z" fill="#334155"/>
      {/* Center cube - visible faces */}
      <path d="M20 16L24 18L20 20L16 18L20 16Z" fill="#475569"/>
      <path d="M20 20L24 18V22L20 24V20Z" fill="#7DD3FC" fillOpacity="0.6"/>
      <path d="M20 20L16 18V22L20 24V20Z" fill="#7DD3FC" fillOpacity="0.4"/>
    </svg>
    <span className={`text-xl font-bold ${dark ? 'text-white' : 'text-[#1E3A5C]'}`}>MedStudyPro</span>
  </div>
);

// Topic icon mapping
const TOPIC_ICONS = {
  hemodynamics: Heart,
  shock: Zap,
  cardiovascular: Heart,
  pharmacology: Droplet,
  respiratory: Activity,
  renal: Droplet,
  default: Brain
};

// Topic descriptions
const TOPIC_DESCRIPTIONS = {
  hemodynamics: "Master the fundamentals of blood flow, cardiac output, and pressure dynamics in critical care settings.",
  shock: "Learn to recognize, classify, and manage all types of shock including septic, cardiogenic, and hypovolemic.",
  cardiovascular: "Deep dive into cardiac conditions, arrhythmias, and advanced cardiac life support protocols.",
  pharmacology: "Essential medications, dosing, interactions, and pharmacokinetics for critical care.",
  respiratory: "Ventilator management, ABG interpretation, and respiratory failure management.",
  renal: "Acute kidney injury, electrolyte management, and renal replacement therapy.",
  default: "Comprehensive clinical knowledge for healthcare professionals."
};

export default function TopicLandingPage() {
  const navigate = useNavigate();
  const [topics, setTopics] = useState([]);
  const [caseStudies, setCaseStudies] = useState([]);
  const [loading, setLoading] = useState(true);
  const [isLoggedIn, setIsLoggedIn] = useState(false);
  const [quizExampleImages, setQuizExampleImages] = useState([]);
  const [currentImageIndex, setCurrentImageIndex] = useState(0);
  const [logoUrl, setLogoUrl] = useState(null);

  useEffect(() => {
    fetchData();
    fetchLogo();
  }, []);

  const fetchLogo = async () => {
    try {
      const res = await fetch(`${API}/api/logo-settings`);
      if (res.ok) {
        const data = await res.json();
        setLogoUrl(data.logo_url || data.logo_light_url);
      }
    } catch (err) {
      console.error('Failed to fetch logo:', err);
    }
  };

  // Helper to render the logo
  const renderLogo = (dark = true) => {
    if (logoUrl) {
      return <BrandLogo logoUrl={getFullUrl(logoUrl)} dark={dark} />;
    }
    return <DefaultLogo dark={dark} />;
  };

  const fetchData = async () => {
    try {
      const [topicsRes, casesRes, quizImageRes] = await Promise.all([
        fetch(`${API}/api/topics/public`),
        fetch(`${API}/api/case-studies`).catch(() => ({ ok: false })),
        fetch(`${API}/api/quiz-example-image`).catch(() => ({ ok: false }))
      ]);
      
      if (topicsRes.ok) {
        const topicsData = await topicsRes.json();
        setTopics(topicsData || []);
      }
      
      if (casesRes.ok) {
        const casesData = await casesRes.json().catch(() => []);
        setCaseStudies(casesData || []);
      }
      
      if (quizImageRes.ok) {
        const imageData = await quizImageRes.json().catch(() => null);
        if (imageData?.quiz_example_images?.length > 0) {
          setQuizExampleImages(imageData.quiz_example_images);
        }
      }
      
      // Check if user is logged in
      try {
        const authRes = await fetch(`${API}/api/auth/me`, { credentials: 'include' });
        setIsLoggedIn(authRes.ok);
      } catch {
        setIsLoggedIn(false);
      }
    } catch (error) {
      console.error('Failed to fetch topics:', error);
      toast.error('Failed to load topics');
    } finally {
      setLoading(false);
    }
  };

  // Helper to get full URL
  const getFullUrl = (url) => {
    if (!url) return null;
    if (url.startsWith('http')) return url;
    if (url.startsWith('/uploads')) return `${API}/api${url}`;
    if (url.startsWith('/')) return `${API}${url}`;
    return `${API}/${url}`;
  };

  // Image carousel navigation
  const nextImage = () => {
    setCurrentImageIndex((prev) => (prev + 1) % quizExampleImages.length);
  };

  const prevImage = () => {
    setCurrentImageIndex((prev) => (prev - 1 + quizExampleImages.length) % quizExampleImages.length);
  };

  const handleLogin = () => {
    navigate('/auth');
  };

  const handleStartPractice = (topicKey) => {
    if (!isLoggedIn) {
      toast.info('Please sign in to start practicing');
      handleLogin();
      return;
    }
    navigate(`/quiz/setup/${encodeURIComponent(topicKey)}`);
  };

  const getTopicIcon = (topicKey) => {
    const key = topicKey?.toLowerCase().replace(/[^a-z]/g, '');
    for (const [keyword, Icon] of Object.entries(TOPIC_ICONS)) {
      if (key?.includes(keyword)) return Icon;
    }
    return TOPIC_ICONS.default;
  };

  const getTopicDescription = (topicKey) => {
    const key = topicKey?.toLowerCase().replace(/[^a-z]/g, '');
    for (const [keyword, desc] of Object.entries(TOPIC_DESCRIPTIONS)) {
      if (key?.includes(keyword)) return desc;
    }
    return TOPIC_DESCRIPTIONS.default;
  };

  const getTopicCases = (topicKey) => {
    return caseStudies.filter(cs => 
      cs.topic?.toLowerCase().includes(topicKey?.toLowerCase()) ||
      cs.category?.toLowerCase().includes(topicKey?.toLowerCase())
    ).slice(0, 2);
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[#192A3F] flex items-center justify-center">
        <Loader2 className="w-10 h-10 animate-spin text-[#FF9E00]" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-b from-[#192A3F] to-[#0D1520]">
      {/* Navigation */}
      <nav className="max-w-7xl mx-auto px-6 py-5 flex items-center justify-between">
        <button onClick={() => navigate('/')}>
          {renderLogo(true)}
        </button>
        <div className="flex items-center gap-6">
          <button 
            onClick={() => navigate('/about')}
            className="text-white/80 hover:text-white text-sm font-medium transition-colors"
          >
            About
          </button>
          <button 
            onClick={() => navigate('/products')}
            className="text-white/80 hover:text-white text-sm font-medium transition-colors"
          >
            Shop
          </button>
          <button 
            onClick={() => navigate('/pricing')}
            className="text-white/80 hover:text-white text-sm font-medium transition-colors"
          >
            Pricing
          </button>
          {isLoggedIn ? (
            <Button
              onClick={() => navigate('/dashboard')}
              className="bg-[#FF9E00] hover:bg-[#E88D00] text-white px-6 py-2 rounded-full font-semibold text-sm"
            >
              Dashboard
            </Button>
          ) : (
            <Button
              onClick={handleLogin}
              className="bg-[#FF9E00] hover:bg-[#E88D00] text-white px-6 py-2 rounded-full font-semibold text-sm"
            >
              Sign In
            </Button>
          )}
        </div>
      </nav>

      {/* Hero Section */}
      <div className="max-w-7xl mx-auto px-6 pt-8 pb-12">
        <button
          onClick={() => navigate('/')}
          className="flex items-center gap-2 text-white/60 hover:text-white text-sm mb-6 transition-colors"
        >
          <ArrowLeft className="w-4 h-4" />
          Back to Home
        </button>
        
        <div className="text-center max-w-3xl mx-auto">
          <div className="inline-flex items-center gap-2 px-4 py-2 bg-[#FF9E00]/20 rounded-full text-[#FF9E00] text-sm font-medium mb-6">
            <Target className="w-4 h-4" />
            Explore Clinical Topics
          </div>
          <h1 className="text-4xl lg:text-5xl font-bold text-white mb-4">
            Master Critical Care, <span className="text-[#FF9E00]">One Topic at a Time</span>
          </h1>
          <p className="text-lg text-white/60 max-w-2xl mx-auto">
            Browse our comprehensive library of clinical topics. Each topic includes adaptive quizzes, 
            interactive games, and real-world case studies designed by ICU clinicians.
          </p>
        </div>
      </div>

      {/* Quiz Example Image Carousel (admin-uploadable) */}
      {quizExampleImages.length > 0 && (
        <div className="max-w-4xl mx-auto px-6 pb-8">
          <div className="relative bg-white rounded-2xl overflow-hidden shadow-xl">
            {/* Full image display - no cropping */}
            <img 
              src={getFullUrl(quizExampleImages[currentImageIndex])} 
              alt={`Quiz Example ${currentImageIndex + 1}`} 
              className="w-full h-auto"
              data-testid="quiz-example-image"
            />
            
            {/* Navigation arrows (only show if multiple images) */}
            {quizExampleImages.length > 1 && (
              <>
                <button
                  onClick={prevImage}
                  className="absolute left-4 top-1/2 -translate-y-1/2 w-10 h-10 bg-black/50 hover:bg-black/70 rounded-full flex items-center justify-center text-white transition-colors"
                  data-testid="quiz-carousel-prev"
                >
                  <ChevronLeft className="w-6 h-6" />
                </button>
                <button
                  onClick={nextImage}
                  className="absolute right-4 top-1/2 -translate-y-1/2 w-10 h-10 bg-black/50 hover:bg-black/70 rounded-full flex items-center justify-center text-white transition-colors"
                  data-testid="quiz-carousel-next"
                >
                  <ChevronRight className="w-6 h-6" />
                </button>
                
                {/* Dot indicators */}
                <div className="absolute bottom-4 left-1/2 -translate-x-1/2 flex gap-2">
                  {quizExampleImages.map((_, idx) => (
                    <button
                      key={idx}
                      onClick={() => setCurrentImageIndex(idx)}
                      className={`w-2 h-2 rounded-full transition-colors ${
                        idx === currentImageIndex ? 'bg-[#FF9E00]' : 'bg-white/50'
                      }`}
                      data-testid={`quiz-carousel-dot-${idx}`}
                    />
                  ))}
                </div>
              </>
            )}
          </div>
        </div>
      )}

      {/* Topics Grid */}
      <div className="max-w-7xl mx-auto px-6 pb-20">
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
          {topics.map((topic) => {
            const topicKey = topic.topic_key || topic.topic_id;
            const topicName = topic.topic_display_name || topic.name || topicKey;
            const subtopics = topic.subtopics || [];
            const Icon = getTopicIcon(topicKey);
            const description = getTopicDescription(topicKey);
            const relatedCases = getTopicCases(topicKey);

            return (
              <div
                key={topicKey}
                data-testid={`topic-card-${topicKey}`}
                className="bg-white rounded-2xl overflow-hidden shadow-xl hover:shadow-2xl transition-all group"
              >
                {/* Topic Header */}
                <div className="bg-gradient-to-r from-[#1E3A5C] to-[#264B73] p-6">
                  <div className="flex items-start justify-between">
                    <div className="w-12 h-12 bg-white/10 rounded-xl flex items-center justify-center">
                      <Icon className="w-6 h-6 text-[#FF9E00]" />
                    </div>
                  </div>
                  <h3 className="text-xl font-bold text-white mt-4">{topicName}</h3>
                  <p className="text-white/70 text-sm mt-2 line-clamp-2">{description}</p>
                </div>

                {/* Topic Content */}
                <div className="p-6">
                  {/* Available Activities */}
                  <div className="mb-4">
                    <p className="text-xs font-semibold text-slate-500 uppercase tracking-wider mb-2">
                      Available Activities
                    </p>
                    <div className="flex flex-wrap gap-2">
                      <span className="inline-flex items-center gap-1 px-3 py-1 bg-blue-50 text-blue-700 text-xs font-medium rounded-full">
                        <BookOpen className="w-3 h-3" />
                        Smart Session
                      </span>
                      <span className="inline-flex items-center gap-1 px-3 py-1 bg-purple-50 text-purple-700 text-xs font-medium rounded-full">
                        <Target className="w-3 h-3" />
                        Skill Builder
                      </span>
                      <span className="inline-flex items-center gap-1 px-3 py-1 bg-green-50 text-green-700 text-xs font-medium rounded-full">
                        <Gamepad2 className="w-3 h-3" />
                        Games
                      </span>
                      <span className="inline-flex items-center gap-1 px-3 py-1 bg-amber-50 text-amber-700 text-xs font-medium rounded-full">
                        <FileText className="w-3 h-3" />
                        Reference Sheets
                      </span>
                      {relatedCases.length > 0 && (
                        <span className="inline-flex items-center gap-1 px-3 py-1 bg-rose-50 text-rose-700 text-xs font-medium rounded-full">
                          <FileText className="w-3 h-3" />
                          Case Studies
                        </span>
                      )}
                    </div>
                  </div>

                  {/* Subtopics Preview */}
                  {subtopics.length > 0 && (
                    <div className="mb-4">
                      <p className="text-xs font-semibold text-slate-500 uppercase tracking-wider mb-2">
                        Includes Subtopics
                      </p>
                      <div className="flex flex-wrap gap-1">
                        {subtopics.slice(0, 3).map((sub, idx) => (
                          <span key={idx} className="text-xs text-slate-600 bg-slate-100 px-2 py-1 rounded">
                            {sub.display || sub}
                          </span>
                        ))}
                        {subtopics.length > 3 && (
                          <span className="text-xs text-slate-400">+{subtopics.length - 3} more</span>
                        )}
                      </div>
                    </div>
                  )}

                  {/* CTAs */}
                  <div className="flex gap-3 pt-4 border-t border-slate-100">
                    <Button
                      data-testid={`start-practice-${topicKey}`}
                      onClick={() => handleStartPractice(topicKey)}
                      className="flex-1 bg-[#FF9E00] hover:bg-[#E88D00] text-white rounded-full text-sm font-semibold"
                    >
                      {isLoggedIn ? (
                        <>
                          <Play className="w-4 h-4 mr-1" />
                          Start Practicing
                        </>
                      ) : (
                        <>
                          <Lock className="w-4 h-4 mr-1" />
                          Sign In to Practice
                        </>
                      )}
                    </Button>
                  </div>
                </div>
              </div>
            );
          })}
        </div>

        {/* Empty State */}
        {topics.length === 0 && (
          <div className="text-center py-16">
            <Brain className="w-16 h-16 text-white/30 mx-auto mb-4" />
            <h3 className="text-xl font-bold text-white mb-2">No Topics Available</h3>
            <p className="text-white/60">Check back soon for new clinical topics!</p>
          </div>
        )}
      </div>

      {/* CTA Section */}
      <div className="bg-[#1E3A5C] py-16">
        <div className="max-w-4xl mx-auto px-6 text-center">
          <h2 className="text-3xl font-bold text-white mb-4">
            Ready to Test Your Clinical Knowledge?
          </h2>
          <p className="text-white/60 text-lg mb-8">
            Join thousands of healthcare professionals who are mastering critical care with MedStudyPro.
          </p>
          <div className="flex flex-wrap gap-4 justify-center">
            {isLoggedIn ? (
              <Button
                onClick={() => navigate('/dashboard')}
                className="bg-[#FF9E00] hover:bg-[#E88D00] text-white h-14 px-10 rounded-full font-semibold"
              >
                Go to Dashboard
              </Button>
            ) : (
              <>
                <Button
                  onClick={handleLogin}
                  className="bg-[#FF9E00] hover:bg-[#E88D00] text-white h-14 px-10 rounded-full font-semibold"
                >
                  Start Free Trial
                </Button>
                <Button
                  onClick={() => navigate('/pricing')}
                  className="bg-white/10 hover:bg-white/20 text-white h-14 px-10 rounded-full font-semibold"
                >
                  View Pricing
                </Button>
              </>
            )}
          </div>
        </div>
      </div>

      {/* Footer */}
      <footer className="bg-[#0D1520] py-12">
        <div className="max-w-7xl mx-auto px-6">
          <div className="flex flex-col md:flex-row justify-between items-center gap-6">
            {renderLogo(true)}
            <div className="flex items-center gap-8">
              <button onClick={() => navigate('/')} className="text-white/60 hover:text-white text-sm">Home</button>
              <button onClick={() => navigate('/about')} className="text-white/60 hover:text-white text-sm">About</button>
              <button onClick={() => navigate('/pricing')} className="text-white/60 hover:text-white text-sm">Pricing</button>
              <button onClick={() => navigate('/contact')} className="text-white/60 hover:text-white text-sm">Contact</button>
            </div>
          </div>
          <div className="mt-8 pt-8 border-t border-white/10 text-center">
            <p className="text-white/40 text-sm">© 2026 MedStudyPro. All rights reserved.</p>
          </div>
        </div>
      </footer>
    </div>
  );
}
